// src/App.js
import React, { useEffect } from "react";
import { installGTM } from "./utils/analytics";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
} from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { AuthProvider } from "./contexts/AuthContext";
import { ToastProvider } from "./contexts/ToastContext";
import Layout from "./components/common/Layout/Layout";
import ScrollToTop from "./components/common/ScrollToTop"; 
import HomePage from "./pages/HomePage";
import AboutPage from "./pages/AboutPage";
import PricingPage from "./pages/PricingPage";
import ContactPage from "./pages/ContactPage";
import LoginPage from "./pages/auth/LoginPage";
import DubbingPage from "./pages/dubbing/DubbingPage";
import AuthCodeHandler from "./components/auth/AuthCodeHandler";
import MagicLinkHandler from "./pages/auth/MagicLinkHandler";
import ProtectedRoute from "./components/common/ProtectedRoute/ProtectedRoute";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import AudioTranslation from "./pages/AudioTranslation";

// New SEO pages
import AIVideoDubbingPage from "./pages/AIVideoDubbingPage";
import VsDeepDubPage from "./pages/vs/VsDeepDubPage";
import VsHeyGenPage from "./pages/vs/VsHeyGenPage";
import VsTraditionalDubbingPage from "./pages/vs/VsTraditionalDubbingPage";
import LanguagesPage from "./pages/languages/LanguagesPage";
import DemoPage from "./pages/DemoPage";
import MBSDemoPage from "./pages/demo/MBSDemoPage";
import UAEDemoPage from "./pages/demo/UAEDemoPage";
import CreatorsPage from "./pages/use-cases/CreatorsPage";
import MediaPage from "./pages/use-cases/MediaPage";
import EdtechPage from "./pages/use-cases/EdtechPage";

// NEW: Analytics page import
import AdminAnalyticsPage from "./pages/AdminAnalyticsPage";

// Import the auth events hook for automatic token management
import useAuthEvents from "./hooks/useAuthEvents";

import "./App.css";
import theme from "./utils/theme";
import SmoothScroll from "./components/common/SmoothScroll";

function AppContent() {
  const navigate = useNavigate();

  // This will handle expired tokens, unauthorized access, and other auth events
  useAuthEvents();

  const handleGetStartedClick = () => {
    navigate("/auth");
  };

  const handleBackToHome = () => {
    navigate("/");
  };

  return (
    <>
      <ScrollToTop /> 
      <Routes>
        <Route
          path="/"
          element={
            <Layout onSignInClick={handleGetStartedClick}>
              <HomePage />
            </Layout>
          }
        />
        <Route
          path="/about-us"
          element={
            <Layout onSignInClick={handleGetStartedClick}>
              <AboutPage />
            </Layout>
          }
        />
        <Route
          path="/pricing"
          element={
            <Layout onSignInClick={handleGetStartedClick}>
              <PricingPage />
            </Layout>
          }
        />
        <Route
          path="/contact"
          element={
            <Layout onSignInClick={handleGetStartedClick}>
              <ContactPage />
            </Layout>
          }
        />
        <Route
          path="/privacy"
          element={
            <Layout onSignInClick={handleGetStartedClick}>
              <PrivacyPage />
            </Layout>
          }
        />
        <Route
          path="/terms"
          element={
            <Layout onSignInClick={handleGetStartedClick}>
              <TermsPage />
            </Layout>
          }
        />
        
        {/* Universal Auth Routes */}
        <Route path="/auth" element={<LoginPage onBack={handleBackToHome} />} />
        <Route path="/signin" element={<LoginPage onBack={handleBackToHome} />} />
        <Route path="/signup" element={<LoginPage onBack={handleBackToHome} />} />
        
        {/* Legacy Auth Handlers */}
        <Route path="/auth/code" element={<AuthCodeHandler />} />
        <Route path="/auth/magic-link/:token" element={<MagicLinkHandler />} />
        
        {/* Protected Routes */}
        <Route
          path="/dubbing"
          element={
            <ProtectedRoute>
              <DubbingPage />
            </ProtectedRoute>
          }
        />

        {/* Admin Routes */}
        <Route
          path="/audio-translation"
          element={
            <ProtectedRoute>
              <AudioTranslation />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin"
          element={
            <ProtectedRoute requiredRole="admin">
              <AdminDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/dashboard"
          element={
            <ProtectedRoute requiredRole="admin">
              <AdminDashboardPage />
            </ProtectedRoute>
          }
        />

        {/* NEW: Admin Analytics Route */}
        <Route
          path="/admin/analytics"
          element={
            <ProtectedRoute requiredRole="admin">
              <AdminAnalyticsPage />
            </ProtectedRoute>
          }
        />

        {/* SEO Pages */}
        <Route
          path="/ai-video-dubbing"
          element={
            <Layout onSignInClick={handleGetStartedClick}>
              <AIVideoDubbingPage />
            </Layout>
          }
        />
        <Route
          path="/demo"
          element={
            <Layout onSignInClick={handleGetStartedClick}>
              <DemoPage />
            </Layout>
          }
        />
        <Route
          path="/mbs"
          element={
            <Layout onSignInClick={handleGetStartedClick}>
              <MBSDemoPage />
            </Layout>
          }
        />
        <Route
          path="/uae"
          element={
            <Layout onSignInClick={handleGetStartedClick}>
              <UAEDemoPage />
            </Layout>
          }
        />

        {/* Comparison Pages */}
        <Route
          path="/vs/deepdub"
          element={
            <Layout onSignInClick={handleGetStartedClick}>
              <VsDeepDubPage />
            </Layout>
          }
        />
        <Route
          path="/vs/heygen"
          element={
            <Layout onSignInClick={handleGetStartedClick}>
              <VsHeyGenPage />
            </Layout>
          }
        />
        <Route
          path="/vs/traditional-dubbing"
          element={
            <Layout onSignInClick={handleGetStartedClick}>
              <VsTraditionalDubbingPage />
            </Layout>
          }
        />

        {/* Language Pages */}
        <Route
          path="/languages"
          element={
            <Layout onSignInClick={handleGetStartedClick}>
              <LanguagesPage />
            </Layout>
          }
        />

        {/* Use-case Pages */}
        <Route
          path="/use-cases/creators"
          element={
            <Layout onSignInClick={handleGetStartedClick}>
              <CreatorsPage />
            </Layout>
          }
        />
        <Route
          path="/use-cases/media"
          element={
            <Layout onSignInClick={handleGetStartedClick}>
              <MediaPage />
            </Layout>
          }
        />
        <Route
          path="/use-cases/edtech"
          element={
            <Layout onSignInClick={handleGetStartedClick}>
              <EdtechPage />
            </Layout>
          }
        />
      </Routes>
    </>
  );
}

function App() {
  useEffect(() => {
    installGTM();
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <ToastProvider>
          <Router>
            <SmoothScroll>
              <div className="App">
                <AppContent />
              </div>
            </SmoothScroll>
          </Router>
        </ToastProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;